/**
 * Row virtualization for `DataTable`. Wraps `@tanstack/react-virtual` plus an
 * `IntersectionObserver`-based infinite-scroll sentinel, so catalog-scale row counts render
 * only their visible rows. Promoted from the media-units catalog's
 * `use-inventory-virtualizer` hook — `estimateRowHeightPx`/`overscan` are now explicit
 * caller-supplied params instead of module-level constants.
 */
import * as React from 'react';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';

const DEFAULT_OVERSCAN = 10;

export interface UseTableVirtualizerParams {
  rowCount: number;
  /** Estimated row height in pixels, used before a row's real height is measured. */
  estimateRowHeightPx: number;
  /** Extra rows rendered above/below the visible window. Defaults to 10. */
  overscan?: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}

/** The value shape `DataTable`'s `virtualizer` prop expects. */
export interface TableVirtualizerBundle {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  virtualItems: VirtualItem[];
  totalSize: number;
}

export function useTableVirtualizer(params: UseTableVirtualizerParams): TableVirtualizerBundle {
  const {
    rowCount,
    estimateRowHeightPx,
    overscan = DEFAULT_OVERSCAN,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = params;

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) void fetchNextPage();
      },
      { root: scrollContainerRef.current, rootMargin: '0px 0px 30% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimateRowHeightPx,
    overscan,
  });

  return {
    scrollContainerRef,
    sentinelRef,
    virtualItems: virtualizer.getVirtualItems(),
    totalSize: virtualizer.getTotalSize(),
  };
}
